/**
 * MarkdownViewer.js
 * Component for viewing and editing markdown documentation
 * Includes download as DOCX functionality
 */

import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { FaDownload, FaEdit, FaSave, FaTimes } from "react-icons/fa";

export default function MarkdownViewer({ markdown, title = "Documentation" }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMarkdown, setEditedMarkdown] = useState(markdown);

  const downloadAsMarkdown = () => {
    const blob = new Blob([editedMarkdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAsDocx = () => {
    // Convert markdown to simple HTML for Word
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.8; max-width: 800px; margin: 0 auto; padding: 40px; }
    h1 { color: #2563eb; border-bottom: 3px solid #2563eb; padding-bottom: 12px; margin: 40px 0 20px; font-size: 32px; }
    h2 { color: #1e40af; margin-top: 32px; margin-bottom: 16px; font-size: 24px; }
    h3 { color: #1e3a8a; margin-top: 24px; margin-bottom: 12px; font-size: 18px; }
    p { margin: 12px 0; color: #374151; }
    ul, ol { margin: 12px 0; padding-left: 28px; }
    li { margin: 8px 0; color: #374151; }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    th, td { border: 1px solid #d1d5db; padding: 12px; text-align: left; }
    th { background-color: #f3f4f6; font-weight: 600; }
    blockquote { border-left: 4px solid #2563eb; padding: 12px 20px; margin: 20px 0; background: #eff6ff; border-radius: 4px; }
    code { background: #f3f4f6; padding: 3px 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; color: #dc2626; }
    strong { font-weight: 600; color: #111827; }
    em { font-style: italic; color: #6b7280; }
  </style>
</head>
<body>
  ${editedMarkdown.replace(/\n/g, "<br>")}
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title
      .replace(/\s+/g, "-")
      .toLowerCase()}-${Date.now()}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(editedMarkdown);
      alert("Markdown copied to clipboard!");
    } catch (err) {
      console.error("Copy error:", err);
      alert("Failed to copy");
    }
  };

  return (
    <div className="markdown-viewer">
      {/* Toolbar */}
      <div className="markdown-toolbar">
        <div className="toolbar-left">
          <h3>{title}</h3>
        </div>
        <div className="toolbar-right">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="btn btn-icon"
            title={isEditing ? "Cancel editing" : "Edit markdown"}
          >
            {isEditing ? (
              <>
                <FaTimes /> Cancel
              </>
            ) : (
              <>
                <FaEdit /> Edit
              </>
            )}
          </button>
          <button
            onClick={downloadAsMarkdown}
            className="btn btn-icon"
            title="Download as Markdown"
          >
            <FaDownload /> Markdown
          </button>
          <button
            onClick={downloadAsDocx}
            className="btn btn-icon"
            title="Download as Word Document"
          >
            <FaDownload /> Word Doc
          </button>
          <button onClick={copyToClipboard} className="btn btn-secondary">
            Copy Markdown
          </button>
        </div>
      </div>

      {/* Content */}
      {isEditing ? (
        <div className="markdown-editor">
          <div className="editor-header">
            <h4>Edit Markdown</h4>
            <span className="char-count">
              {editedMarkdown.length} characters
            </span>
          </div>
          <textarea
            value={editedMarkdown}
            onChange={(e) => setEditedMarkdown(e.target.value)}
            className="markdown-textarea"
            spellCheck={true}
          />
          <div className="editor-actions">
            <button
              onClick={() => setIsEditing(false)}
              className="btn btn-primary"
            >
              <FaSave /> Save Changes
            </button>
          </div>
        </div>
      ) : (
        <div className="markdown-content">
          <ReactMarkdown>{editedMarkdown}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}
